import {isEqual} from 'lodash';

import {areArraysShallowEqual} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {
  PreviewBuilderModel,
  PreviewerLinkTypesEnum,
  PreviewerModel,
  UnsavedPreviewerModel,
} from '../../../models/api/previewerModel';
import {CustomFields} from '../../editors/commons/customFieldsModel';
import {Expression, serializeExpression} from '../../editors/expressions/expressionModel';
import {ExpressionState} from '../../editors/expressions/expressionState';
import {typeAndParamsEqual} from '../../rules/ruleActionConditionHelpers';
import {ClientRuleCondition} from '../../rules/ruleConditionModel';
import {computePreviewerEditorFormErrors} from '../../tools/previewers/previewerEditorFormErrors';
import {buildCompoundConditions, parseCompoundConditions} from '../conditions/compoundConditionModel';
import {PreviewerResult} from '../expressions/infer/expressionResultModel';

export interface PreviewerState {
  linkType: PreviewerLinkTypesEnum;
  domain: string;
  description?: string;
  exampleUrl?: string;
  previewBuilder: PreviewBuilderModel;
  conditions: ReadonlyArray<ReadonlyArray<ClientRuleCondition>>;
}

export interface PreviewerEditorState extends ExpressionState {
  previewerState: PreviewerState;
  initialPreviewer?: PreviewerModel;
}

export const PreviewerEditorState = {
  get default(): PreviewerEditorState {
    return {
      ...ExpressionState.makeDefault(PreviewerResult),
      previewerState: {
        linkType: PreviewerLinkTypesEnum.ORDER,
        domain: '',
        description: undefined,
        exampleUrl: undefined,
        previewBuilder: {
          name: '',
          canonicalUrl: undefined,
          description: undefined,
          status: undefined,
          ownerId: undefined,
        },
        conditions: [],
      },
    };
  },

  createInitialState(
    initialPreviewer: PreviewerModel | undefined,
    customFields: CustomFields,
  ): PreviewerEditorState {
    if (!initialPreviewer) {
      return {...this.default};
    }

    return {
      ...this.default,
      ...ExpressionState.createInitialState(initialPreviewer.expressions, customFields, PreviewerResult, {}),
      initialPreviewer,
      previewerState: {
        linkType: initialPreviewer.linkType,
        domain: initialPreviewer.domain,
        description: initialPreviewer.description,
        exampleUrl: initialPreviewer.exampleUrl,
        previewBuilder: initialPreviewer.previewBuilder,
        conditions: parseCompoundConditions(initialPreviewer.compoundConditions),
      },
      hasDryRunTestData: Boolean(initialPreviewer.exampleUrl),
    };
  },

  toPreviewer(state: PreviewerEditorState): UnsavedPreviewerModel {
    const {previewerState, expressions} = state;

    const compoundConditions = buildCompoundConditions(previewerState.conditions);

    return {
      linkType: previewerState.linkType,
      domain: previewerState.domain,
      description: previewerState.description,
      exampleUrl: previewerState.exampleUrl,
      expressions: expressions.map(serializeExpression),
      compoundConditions,
      previewBuilder: previewerState.previewBuilder,
    };
  },

  hasDiff(a: PreviewerEditorState, b: PreviewerEditorState) {
    if (a === b) {
      return false;
    }

    const aState = a.previewerState;
    const bState = b.previewerState;

    if (aState.linkType !== bState.linkType) {
      return true;
    }

    if (aState.domain !== bState.domain) {
      return true;
    }

    if (aState.description !== bState.description) {
      return true;
    }

    if (aState.exampleUrl !== bState.exampleUrl) {
      return true;
    }

    if (!areArraysShallowEqual(a.expressions, b.expressions, Expression.equals)) {
      return true;
    }

    if (!isEqual(aState.previewBuilder, bState.previewBuilder)) {
      return true;
    }

    if (
      !areArraysShallowEqual(aState.conditions, bState.conditions, (condsA, condsB) =>
        areArraysShallowEqual(condsA, condsB, typeAndParamsEqual),
      )
    ) {
      return true;
    }

    return false;
  },

  computeFormErrors(state: PreviewerEditorState) {
    return computePreviewerEditorFormErrors(state);
  },
};
